import { Component, computed, inject, input, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { XtItemColumn, XtItemRaw, XtItemRow } from '../../core/models';

// Per-location XT items tab — the merchant's item master read straight off the XtSyncDb replica.
// Embedded in the merchant detail page (beside Products) and in the standalone /xt-items page, which
// adds a merchant picker. No route of its own.
//
// NOT the Products tab. Products is the platform's event-sourced catalog: what the middleware OBSERVED
// from XT and projected. This is the replicated XT table itself, unprojected, which is exactly what you
// want open when the two disagree — it answers "what does XT actually say" rather than "what did we
// record it saying".
//
// READ ONLY, and there is nothing to save: the replica is populated by another process, so anything typed
// here would be erased by the next replication pass.
//
// locationId arrives from the parent as a signal input, and the FIRST fetch is owned by the [lazy]
// p-table's onLazyLoad (fires on render) — the same shape as ProductsTab. Both hosts mount this component
// only once a location exists, so the table cannot render, and therefore cannot fire, without one; the
// guard in load() covers the render that follows a location being cleared. That matters more here than
// elsewhere: the item table holds every location's catalogue in one heap (plausibly hundreds of thousands
// of rows), the location is part of the API path rather than an optional filter, and there is deliberately
// no way to spell "all of them".
//
// The intro copy here is what is true in BOTH hosts. The contrast with Products is stated by the host,
// because what sits next to this table differs: a sibling tab on the merchant page, a separate page under
// Administration otherwise.
@Component({
    selector: 'app-xt-items-tab',
    standalone: true,
    imports: [
        ButtonModule, TableModule, DialogModule, MessageModule,
        InputTextModule, ToggleSwitchModule, FormsModule, DecimalPipe
    ],
    template: `
        <div class="flex flex-wrap items-center gap-2 mb-3">
            <input pInputText [(ngModel)]="search" (keyup.enter)="reload()"
                   placeholder="Search SKU or description" class="w-72" />
            <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
            <p-button icon="pi pi-refresh" [text]="true" size="small" [loading]="loading()"
                      (onClick)="reload()" />
        </div>

        <!-- Server-side paging, always: [lazy] means the table asks for one page at a time and never holds
             the set. [totalRecords] is the server's count for the CURRENT location AND search — a page of
             50 rows looks identical whether the location has 60 items or 60,000, and a total that ignored
             the search would offer pages that come back empty. -->
        <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true"
                 [rows]="pageSize" [totalRecords]="total()" [first]="first()"
                 (onLazyLoad)="onPage($event)" [showCurrentPageReport]="true"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                 styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
                <tr>
                    <th class="w-28">Item ID</th>
                    <th>Location</th>
                    <!-- Named, not just "SKU": XT carries primary_sku AND linksku, and which one a
                         downstream system used is the first question when a product goes missing. -->
                    <th pSortableColumn="sku">SKU <span class="text-surface-400 font-normal text-xs">(primary_sku)</span> <p-sortIcon field="sku" /></th>
                    <th pSortableColumn="price" class="w-32">Price <p-sortIcon field="price" /></th>
                    <th pSortableColumn="description">Description <p-sortIcon field="description" /></th>
                    <th class="w-12"></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-i>
                <tr class="cursor-pointer" (click)="open(i)">
                    <td class="tabular-nums font-medium">{{ i.itemId }}</td>
                    <td class="text-sm font-mono">{{ i.locationId }}</td>
                    <td class="font-medium whitespace-nowrap">{{ i.primarySku || '—' }}</td>
                    <td class="tabular-nums whitespace-nowrap">
                        @if (i.price === null || i.price === undefined) {
                            <span class="text-surface-400">—</span>
                        } @else {
                            {{ i.price | number: '1.2-2' }}
                        }
                    </td>
                    <td class="text-sm">{{ i.description || '—' }}</td>
                    <td>
                        <p-button icon="pi pi-eye" [text]="true" size="small"
                                  (onClick)="open(i)" (click)="$event.stopPropagation()" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="6">
                        <!-- ranSearch(), not the box: the message describes the request that came back
                             empty, not whatever has been typed since. -->
                        {{ error() ?? (ranSearch() ? 'No item matches “' + ranSearch() + '” at this merchant.'
                                                   : 'No items for this merchant.') }}
                    </td>
                </tr>
            </ng-template>
        </p-table>

        <!-- THE ROW, and nothing about the row. Column name on the left, stored value on the right, in
             one order (alphabetical, the server's) with no headings between them. The content scrolls:
             163 rows is taller than any viewport. -->
        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '54rem' }" [dismissableMask]="true"
                  [contentStyle]="{ 'max-height': '70vh', overflow: 'auto' }"
                  [header]="'XT item ' + (selected()?.itemId ?? '')">
            @if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detailError()) {
                <p-message severity="error">{{ detailError() }}</p-message>
            } @else if (detail(); as d) {
                <div class="flex flex-wrap items-center gap-3 mb-3">
                    <span class="font-mono text-sm">{{ d.table }}</span>
                    <span class="text-xs text-surface-500 dark:text-surface-400">
                        {{ rows().length }} of {{ d.columns.length }} columns
                    </span>

                    <span class="ml-auto flex items-center gap-2">
                        <!-- Kept, and it means null or blank ONLY: a 0 and a false are values XT set and
                             are always on screen. A real row leaves most of its 163 columns null, and
                             they are all scrolled past on the way to what you came for. -->
                        <span class="text-sm text-surface-500 dark:text-surface-400">Hide null / blank</span>
                        <p-toggleswitch [ngModel]="hideEmpty()" (onChange)="hideEmpty.set($event.checked)" />
                        <!-- The WHOLE row, not the filtered view: raw data ends up pasted somewhere, and
                             what gets pasted should not depend on a toggle. -->
                        <p-button [label]="copied() ? 'Copied' : 'Copy JSON'" icon="pi pi-copy"
                                  size="small" [outlined]="true" (onClick)="copy(d)" />
                    </span>
                </div>

                <table class="w-full text-sm font-mono">
                    <tbody>
                        @for (c of rows(); track c.column) {
                            <tr class="border-b border-surface-100 dark:border-surface-800 align-top">
                                <td class="py-1 pr-6 w-64 text-surface-500 dark:text-surface-400 whitespace-nowrap">
                                    {{ c.column }}
                                </td>
                                <!-- Interpolation prints the value as it is: true, 0, 21.99, the ISO
                                     timestamp. No pipes here, on purpose. null is the one thing that has
                                     no text of its own, so it gets the word. -->
                                <td class="py-1 break-all">
                                    @if (c.value === null) {
                                        <span class="text-surface-400 dark:text-surface-500 italic">null</span>
                                    } @else {
                                        {{ c.value }}
                                    }
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            }
        </p-dialog>
    `
})
export class XtItemsTab {
    private readonly api = inject(MiddlewareApi);

    /** Signal input — the parent (merchant-detail tabs, or the standalone page's picker) passes
     *  [locationId]. Required: this component never renders without a merchant to scope to. */
    readonly locationId = input.required<string>();

    readonly items = signal<XtItemRow[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    /** Starts true: the table fires onLazyLoad on its first render, so the honest state between mount and
     *  that first response is "loading", not "the replica holds no items for this merchant". */
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly pageSize = 50;

    search = '';

    /** The search term the rows on screen were fetched with. Separate from the input's model so the empty
     *  message reports the request that came back empty rather than the half-typed next one. */
    readonly ranSearch = signal('');

    // Column sort echoed to the server (whitelisted there: sku / price / description). Plain fields, not
    // signals: only the loader reads them, and it re-runs on every table event.
    private sortField: string | null = null;
    private sortDesc = false;

    /** The request on the wire, as merchant + page + search + sort.
     *
     *  A lazy table has TWO things that can ask for the same page: this component (Search, Refresh) and
     *  PrimeNG's own onLazyLoad. Whether moving `[first]` back to zero also emits is an internal detail of
     *  the table version, so this makes it not matter — the second ask for a page already being fetched is
     *  dropped, and no ask is ever silently lost. */
    private inFlight: string | null = null;

    readonly showDetail = signal(false);
    readonly selected = signal<XtItemRow | null>(null);
    readonly detail = signal<XtItemRaw | null>(null);
    readonly detailLoading = signal(false);
    readonly detailError = signal<string | null>(null);

    /** A SIGNAL, not a plain field: the app is zoneless, and this one is read by the template to decide
     *  which rows exist. On by default — a real item populates a small fraction of 163 columns, so the
     *  unfiltered view is mostly nulls. */
    readonly hideEmpty = signal(true);

    /** Set by a successful copy, cleared when another item is opened. Inline on the button, no toast. */
    readonly copied = signal(false);

    /** The rows on screen. EMPTY IS null OR A BLANK STRING, and nothing else: a 0, a false and an epoch
     *  date are values XT set, and hiding them would turn "not taxable" into "we have no idea". The
     *  server no longer collapses blank to null — this is the only place that has an opinion about it. */
    readonly rows = computed<XtItemColumn[]>(() => {
        const columns = this.detail()?.columns ?? [];
        return this.hideEmpty()
            ? columns.filter((c) => c.value !== null && !(typeof c.value === 'string' && c.value.trim() === ''))
            : columns;
    });

    onPage(e: { first?: number | null; rows?: number | null; sortField?: string | string[] | null; sortOrder?: number | null }): void {
        // The [lazy] table reports paging AND sorting through this one event. sortOrder is 1 asc / -1 desc;
        // PrimeNG types sortField as string|string[] for its multi-sort mode, and this grid is single-sort.
        this.sortField = Array.isArray(e.sortField) ? (e.sortField[0] ?? null) : (e.sortField ?? null);
        this.sortDesc = e.sortOrder === -1;
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    /** Back to page one — what Search and Refresh mean. Both halves are needed: the paginator is showing
     *  "51-100 of …" of a list that no longer exists, and page one has to be fetched. If moving `[first]`
     *  also makes the table ask, that ask is dropped as already in flight. */
    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        // The component is only mounted when a merchant exists, but onLazyLoad can still fire on the
        // render that follows one being cleared. No merchant, no request.
        const loc = this.locationId();
        if (!loc) { this.loading.set(false); return; }

        const term = this.search.trim();
        const asking = `${loc}|${page}|${term}|${this.sortField ?? ''}|${this.sortDesc}`;
        if (this.inFlight === asking) return;
        this.inFlight = asking;

        this.loading.set(true);
        this.error.set(null);

        this.api.getLocationXtItems(loc, {
            page,
            pageSize: this.pageSize,
            search: term || undefined,
            sort: this.sortField || undefined,
            desc: this.sortDesc || undefined
        }).subscribe({
            next: (r) => {
                this.items.set(r.items ?? []);
                // The server's count for THIS location and search. Never items().length — that is the size
                // of one page and would collapse the paginator to a single page.
                this.total.set(r.total ?? 0);
                this.ranSearch.set(term);
                this.loading.set(false);
                this.inFlight = null;
            },
            error: (e) => {
                this.items.set([]);
                this.total.set(0);
                this.ranSearch.set(term);
                this.error.set(this.describe(e));
                this.loading.set(false);
                this.inFlight = null;
            }
        });
    }

    open(row: XtItemRow): void {
        const loc = this.locationId();
        if (!loc) return;

        this.showDetail.set(true);
        this.selected.set(row);
        this.detail.set(null);
        this.detailError.set(null);
        this.detailLoading.set(true);
        this.copied.set(false);

        this.api.getLocationXtItem(loc, row.itemId).subscribe({
            next: (d) => { this.detail.set(d); this.detailLoading.set(false); },
            error: (e) => { this.detailError.set(this.describe(e)); this.detailLoading.set(false); }
        });
    }

    /** The row as one JSON object, column name to stored value — the shape you would paste into a ticket
     *  or a scratch file. Every column, in the order shown, whatever the toggle is doing. */
    copy(row: XtItemRaw): void {
        const json = JSON.stringify(Object.fromEntries(row.columns.map((c) => [c.column, c.value])), null, 2);
        navigator.clipboard?.writeText(json).then(
            () => this.copied.set(true),
            // A clipboard the browser refuses is not worth an error banner — the values are selectable.
            () => this.copied.set(false)
        );
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        // The 404 this screen can produce is a sentence worth showing verbatim: it distinguishes "not an
        // onboarded merchant" from "this merchant has no items", which send you to different places.
        const server = e?.error?.error ?? e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
